import { memo, useCallback, useState } from "react";
import { Box, IconButton, Typography, TextField, styled, Button } from "@mui/material";
import { Info,  Pencil, Save, X } from "lucide-react";

// DnD
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";

// Components
import DimmerLoader from "./common/DimmerLoader";
import CardGenerateVideoAssetItem from "./CardGenerateVideoAssetItem";
import { hydrateVoicePrompt, synchronizeVoicePrompt } from "./common/voicePrompt";
import { deriveSceneDescription } from "./scriptPayload";

// Types
import {
  GeneratedVideoItem,
  UpdateCompositionPayload,
  UpdateItemPayload,
} from "./types/MeGenTypes";

// ============================================================
// Types
// ============================================================

type CardGenerateVideoAssetsProps = {
  items: GeneratedVideoItem[];
  onUpdateItem?: (
    threadId: string,
    itemId: string,
    data: Omit<UpdateItemPayload, "item_id">
  ) => void;
  onUpdateComposition?: (
    threadId: string,
    data: UpdateCompositionPayload,
    onSuccess?: () => void
  ) => void;
  threadId: string | null;
  mediaVersion?: string;
  isRunning?: boolean;
};

// ============================================================
// Styled Components
// ============================================================

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  border: "1px solid #e2e8f0",
  padding: "1.5rem",
  marginBottom: "1.5rem",
});

const AssetsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
  gap: "1rem",
});

const EditModeToolbar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0.625rem 1rem",
  backgroundColor: "#fdf2f8",
  border: "1px solid #f9a8d4",
  borderRadius: "0.5rem",
  marginBottom: "1rem",
});

const SaveButton = styled(Button)({
  backgroundColor: "#16a34a",
  color: "white",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "5rem",
  padding: "0.375rem 1rem",
  "&:hover": { backgroundColor: "#15803d" },
});

const CancelButton = styled(Button)({
  color: "#dc2626",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "5rem",
  padding: "0.375rem 1rem",
  border: "1px solid #fecaca",
  backgroundColor: "#fff5f5",
  "&:hover": { backgroundColor: "#fee2e2" },
});

const EditModeToggleButton = styled(Button)({
  color: "white",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.875rem",
  borderRadius: "5rem",
  border: "1px solid #e2e8f0",
  padding: "0.375rem 2rem",
  backgroundColor: "#db2777",
  "&:hover": { backgroundColor: "#be185d" },
});

// ============================================================
// SortableItem — wraps AssetItem with dnd-kit useSortable
// ============================================================

type SortableItemProps = {
  item: GeneratedVideoItem;
  isActive: boolean;
  mediaVersion?: string;
  onActiveChange: (id: string, active: boolean) => void;
};

const SortableItem = memo(
  ({ item, isActive, mediaVersion, onActiveChange }: SortableItemProps) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: String(item.id) });

    const style: React.CSSProperties = {
      transform: DndCSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.45 : 1,
      zIndex: isDragging ? 999 : undefined,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <CardGenerateVideoAssetItem
          item={item}
          mediaVersion={mediaVersion}
          onEditClick={() => {}}
          isEditMode={true}
          isActive={isActive}
          onActiveChange={onActiveChange}
          dragHandleProps={{ ...attributes, ...listeners }}
        />
      </div>
    );
  }
);

SortableItem.displayName = "SortableItem";

// ============================================================
// Main Component
// ============================================================

const CardGenerateVideoAssets = (props: CardGenerateVideoAssetsProps) => {

  // ---- Item (script/headline/image) Edit State ----
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Omit<UpdateItemPayload, "item_id">>({});
  // style ของ scene ที่กำลังแก้ — ต้องรู้ตอนประกอบ scene_description กลับ
  const [editingStylePrompt, setEditingStylePrompt] = useState<string>("");

  // ---- Composition Edit Mode (order + active) ----
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [draftItems, setDraftItems] = useState<GeneratedVideoItem[]>([]);

  // ---- DnD Sensors ----
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================================
  // Item Edit Handlers
  // ============================================================

  const handleEditClick = (item: any) => {
    const voice = hydrateVoicePrompt(item);
    setEditingItem(String(item.id));
    setEditingStylePrompt(item.image_style_prompt || "");
    setEditForm({
      headline: item.headline,
      script: voice.script,
      voice_guide: voice.voice_guide,
      voice_script: voice.voice_script,
      image_prompt: item.image_prompt,
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditForm({});
  };

  const handleSaveEdit = () => {
    if (!props.threadId || !editingItem) return;
    // "Image Prompt (Regenerates Image)" แก้เฉพาะ image_prompt ไม่ได้: run ถัดไปประกอบ image_prompt
    // กลับจาก scene_description ถ้าปล่อย scene ค้างไว้ ค่าที่ผู้ใช้พิมพ์จะถูกลบทิ้งในการ generate ครั้งถัดไป
    const payload: UpdateItemPayload = { ...synchronizeVoicePrompt(editForm) };
    if (payload.image_prompt !== undefined) {
      payload.scene_description = deriveSceneDescription(payload.image_prompt, editingStylePrompt);
    }
    props.onUpdateItem?.(props.threadId, editingItem, payload);
    setEditingItem(null);
    setEditForm({});
    setEditingStylePrompt("");
  };

  // ============================================================
  // Composition Edit Mode Handlers
  // ============================================================

  const handleEnterEditMode = () => {
    // copy props.items ไป draft พร้อม default active = true
    const draft = props.items.map((item) => ({
      ...item,
      active: item.active ?? true,
    }));
    setDraftItems(draft);
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    const confirmed = window.confirm("ต้องการยกเลิกการแก้ไขหรือไม่?");
    if (!confirmed) return;

    setDraftItems([]);
    setIsEditMode(false);
  };

  const handleActiveChange = useCallback((id: string, active: boolean) => {
    setDraftItems((prev) =>
      prev.map((item) => (String(item.id) === id ? { ...item, active } : item))
    );
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftItems((prev) => {
      const oldIndex = prev.findIndex((item) => String(item.id) === String(active.id));
      const newIndex = prev.findIndex((item) => String(item.id) === String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleSaveComposition = () => {
    const confirmed = window.confirm("ต้องการบันทึกการแก้ไขหรือไม่?");
    if (!confirmed) return;

    if (!props.threadId) return;
    const payload: UpdateCompositionPayload = {
      items: draftItems.map((item, index) => ({
        id: String(item.id),
        active: item.active ?? true,
        seq: index + 1,
      })),
      render_mode: "review",
    };
    props.onUpdateComposition?.(props.threadId, payload, () => {
      setIsEditMode(false);
      setDraftItems([]);
    });
  };

  // ============================================================
  // Render
  // ============================================================

  // ในโหมดปกติ ซ่อน item ที่ active = false
  const visibleItems = props.items.filter((item) => item.active !== false);

  return (
    <Card id="megen-assets-root" sx={{ position: "relative", overflow: "hidden" }}>
      <DimmerLoader active={!!props.isRunning} text={"Generating Assets..."} inverted size="medium" />

      {/* ===== Header ===== */}
      <Box id="megen-assets-header" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography id="megen-assets-title" sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#1e293b" }}>
          Generated Assets
        </Typography>

        {!isEditMode ? (
          <EditModeToggleButton
            id="megen-assets-edit-mode-btn"
            startIcon={<Pencil size={15} />}
            onClick={handleEnterEditMode}
            disabled={!!props.isRunning || props.items.length === 0}
          >
            Edit Order & Visibility
          </EditModeToggleButton>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <CancelButton id="megen-assets-cancel-btn" onClick={handleCancelEditMode}>Cancel</CancelButton>
            <SaveButton id="megen-assets-save-btn" onClick={handleSaveComposition}>Save Changes</SaveButton>
          </Box>
        )}
      </Box>

      {/* ===== Edit Mode: Grid with DnD ===== */}
      {isEditMode ? (
        <>
          {/* Toolbar hint */}
          <EditModeToolbar id="megen-assets-edit-toolbar">
            <Typography sx={{ display: "flex", alignItems: "center", fontSize: "0.8125rem", color: "#be185d", fontWeight: 500, gap: "4px" }}>
              <span>
                <Info size={15} style={{ marginTop: "4px" }} />
              </span>
              <span>
                ลากที่ไอคอน ⠿ เพื่อเรียงลำดับ — Checkbox เพื่อเปิด/ปิดการใช้งาน
              </span>
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9d174d", fontWeight: 500 }}>
              {draftItems.filter((i) => i.active ?? true).length} / {draftItems.length} active
            </Typography>
          </EditModeToolbar>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftItems.map((i) => String(i.id))}
              strategy={rectSortingStrategy}
            >
              {/* ใช้ Grid เหมือนโหมดปกติ */}
              <AssetsGrid id="megen-assets-grid">
                {draftItems.map((item) => (
                  <SortableItem
                    key={String(item.id)}
                    item={item}
                    isActive={item.active ?? true}
                    mediaVersion={props.mediaVersion}
                    onActiveChange={handleActiveChange}
                  />
                ))}
              </AssetsGrid>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        /* ===== Normal Mode: Grid (ซ่อน inactive) ===== */
        <AssetsGrid id="megen-assets-grid">
          {visibleItems.map((item: any) => {
            const isEditing = editingItem === String(item.id);

            if (isEditing) {
              return (
                <Box
                  key={item.id}
                  sx={{
                    border: "2px solid #3b82f6",
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    backgroundColor: "#eff6ff",
                    p: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#2563eb", textTransform: "uppercase" }}>
                      Editing
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={handleSaveEdit}
                        sx={{ bgcolor: "#2563eb", color: "white", "&:hover": { bgcolor: "#1d4ed8" } }}
                      >
                        <Save size={14} />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelEdit} sx={{ bgcolor: "#e2e8f0" }}>
                        <X size={14} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Headline
                    </Typography>
                    <TextField
                      fullWidth size="small"
                      value={editForm.headline || ""}
                      onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })}
                    />
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Voice Guide (Regenerates Audio)
                    </Typography>
                    <TextField
                      fullWidth multiline rows={3} size="small"
                      value={editForm.voice_guide || ""}
                      onChange={(e) => setEditForm({ ...editForm, voice_guide: e.target.value })}
                      placeholder="Style: ...\nTone: ..."
                      sx={{ "& .MuiInputBase-inputMultiline": { resize: "vertical" } }}
                    />
                  </Box>

                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Voice Script (Regenerates Audio)
                    </Typography>
                    <TextField
                      fullWidth multiline rows={3} size="small"
                      value={editForm.voice_script || ""}
                      onChange={(e) => setEditForm({ ...editForm, voice_script: e.target.value })}
                      sx={{ "& .MuiInputBase-inputMultiline": { resize: "vertical" } }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600, mb: 0.5 }}>
                      Image Prompt (Regenerates Image)
                    </Typography>
                    <TextField
                      fullWidth multiline rows={2} size="small"
                      value={editForm.image_prompt || ""}
                      onChange={(e) => setEditForm({ ...editForm, image_prompt: e.target.value })}
                      sx={{ "& .MuiInputBase-inputMultiline": { resize: "vertical" } }}
                    />
                  </Box>
                </Box>
              );
            }

            return (
              <CardGenerateVideoAssetItem
                key={item.id}
                item={item}
                onEditClick={handleEditClick}
                mediaVersion={props.mediaVersion}
              />
            );
          })}
        </AssetsGrid>
      )}
    </Card>
  );
};

CardGenerateVideoAssets.displayName = "CardGenerateVideoAssets";

export default memo(CardGenerateVideoAssets);
